/**
 * EncryptTextUseCase
 *
 * Pure-domain wrapper around `textEncryptionService.encrypt`. Pair-of-three
 * with `InitializeTextEncryptionUseCase` and `DecryptTextUseCase`. Callers
 * (use cases that own a chat write hop) invoke
 * `encryptTextUseCase.execute(plaintext)` instead of threading an
 * `encryptContent` callback or reaching into the service directly.
 *
 * Pre-condition: the singleton's AES-GCM key has been derived for the
 * intended peer via `InitializeTextEncryptionUseCase.execute`. When the key
 * is null (never-initialized, peer had no `publicJwk`, or derivation failed)
 * the service returns the input unchanged — Flutter parity:
 * `e2ee_impl.dart:65-67`.
 *
 * Flutter parity: `chat_repository.dart:19` —
 * `e2ee.encrypt(content)` invoked exactly once when `content` is non-null
 * and non-empty. The string-empty/short-circuit guard stays at the call
 * site (e.g., `SendTextChatMessageUseCase`); this use case unconditionally
 * forwards whatever the caller hands it.
 */

import {
  TextEncryptionService,
  textEncryptionService,
} from '@/domain/services/textEncryption/textEncryptionService';

export class EncryptTextUseCase {
  private textEncryptionService: TextEncryptionService;

  constructor(service: TextEncryptionService = textEncryptionService) {
    this.textEncryptionService = service;
  }

  /**
   * Encrypt `plaintext` using the singleton's currently-derived AES-GCM key.
   * Returns the ciphertext as one UTF-16 code unit per byte (matches Flutter's
   * `String.fromCharCodes(encryptedBytes)`). When the derived key is null
   * (pass-through mode) returns `plaintext` unchanged.
   */
  async execute(plaintext: string): Promise<string> {
    return this.textEncryptionService.encrypt(plaintext);
  }
}

export const encryptTextUseCase = new EncryptTextUseCase();
